import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtHora, hoyISO } from "@/lib/datetime";
import { getBestGeoFix } from "@/lib/geo";
import PageHeader from "@/components/PageHeader";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { SignIn, SignOut, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

// Portal del docente (solo escuelas): checa entrada y salida por bloque de clases
// (varias veces al día), ve sus clases de hoy con su estatus, su horario y su quincena.
// Backend: routes/portal_docente.py.

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const ESTATUS = {
  impartida: ["Impartida", "bg-emerald-100 text-emerald-800"],
  retardo: ["Retardo", "bg-amber-100 text-amber-800"],
  salida_anticipada: ["Salida anticipada", "bg-amber-100 text-amber-800"],
  falta: ["Falta", "bg-red-100 text-red-800"],
  sin_entrada: ["Sin entrada", "bg-orange-100 text-orange-800"],
  sin_salida: ["Sin salida", "bg-orange-100 text-orange-800"],
  justificada: ["Justificada", "bg-blue-100 text-blue-800"],
  pendiente: ["Pendiente", "bg-slate-100 text-slate-600"],
  inhabil: ["Día inhábil", "bg-violet-100 text-violet-800"],
  suspendida: ["Suspendida", "bg-violet-100 text-violet-800"],
};

function Chip({ estatus }) {
  const [label, cls] = ESTATUS[estatus] || [estatus, "bg-slate-100"];
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${cls}`}>{label}</span>;
}

function inicioQuincena() {
  const hoy = hoyISO();
  return `${hoy.slice(0, 8)}${Number(hoy.slice(8)) <= 15 ? "01" : "16"}`;
}

const TOUR_STORAGE_KEY = "chkr_tour_portal_docente_seen";

const TOUR_STEPS = [
  { target: "[data-tour='docente-checkin']", title: "Registrar entrada y salida", content: "Si tu escuela lo permite, aquí checas desde el celular o la computadora; también puedes checar en el checador del plantel. Según las reglas de tu escuela, checas al inicio y al final de cada bloque de clases seguidas, o en cada clase. Si tu escuela pide ubicación, una marca hecha lejos del plantel se guarda pero no cuenta." },
  { target: "[data-tour='docente-quincena']", title: "Tu quincena", content: "Tus horas-clase programadas, impartidas, faltas y las que vas a cobrar. \"Por revisar\" quiere decir que te faltó checar la entrada o la salida de un bloque: acude con RH." },
  { target: "[data-tour='docente-hoy']", title: "Tus clases de hoy", content: "Cada clase del día con su estado: impartida, retardo, falta, justificada o suplencia." },
  { target: "[data-tour='docente-horario']", title: "Tu horario", content: "Tu carga de la semana en el ciclo actual. Si algo no coincide con lo que impartes, avisa a coordinación o a RH." },
];

export default function PortalDocente() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [hoy, setHoy] = useState(null);
  const [quincena, setQuincena] = useState(null);
  const [carga, setCarga] = useState(null);
  const [busy, setBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState(null);
  const [now, setNow] = useState(new Date());
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const { data: e } = await api.get("/escuela/mi-estado");
      setEstado(e);
      const [h, q, c] = await Promise.all([
        api.get("/escuela/mi-asistencia", { params: { fecha_desde: e.fecha, fecha_hasta: e.fecha } }),
        api.get("/escuela/mi-asistencia", { params: { fecha_desde: inicioQuincena(), fecha_hasta: e.fecha } }),
        api.get("/escuela/mi-carga"),
      ]);
      setHoy(h.data);
      setQuincena(q.data);
      setCarga(c.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const tz = estado?.zona_horaria;

  const checar = async () => {
    const tipo = estado.siguiente;
    setBusy(true);
    let geo = {};
    setGeoMsg("Obteniendo ubicación…");
    try {
      const pos = await getBestGeoFix({ onUpdate: (acc) => setGeoMsg(`Obteniendo ubicación… (precisión: ${Math.round(acc)}m)`) });
      if (pos) geo = { geo_lat: pos.coords.latitude, geo_lng: pos.coords.longitude };
    } catch {}
    setGeoMsg(null);
    try {
      const { data } = await api.post("/escuela/checkin", { tipo, ...geo });
      const clase = data.clase ? ` · ${data.clase.etiqueta} · ${data.clase.hora_inicio}` : "";
      if (!data.cuenta_para_clases) {
        toast.warning(`Marca guardada, pero estás a ${Math.round(data.distancia_metros)} m del plantel: no cuenta para tus clases. Checa al llegar o usa el checador.`, { duration: 10000 });
      } else {
        toast.success(`${tipo === "entrada" ? "Entrada" : "Salida"} registrada${clase}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clasesHoy = hoy?.dias?.[0]?.clases || [];
  const sugerida = estado?.clase_sugerida;
  const n = hoy?.nombres || { materias: {}, grupos: {}, centros: {} };
  const porDia = (carga?.clases || []).reduce((acc, c) => ({ ...acc, [c.dia]: [...(acc[c.dia] || []), c] }), {});

  return (
    <div className="p-6 md:p-10" data-testid="portal-docente">
      <PageHeader
        kicker={`Hola, ${user?.name?.split(" ")[0] || ""}`}
        title="Mis clases"
        subtitle={now.toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric", ...(tz ? { timeZone: tz } : {}) })}
        actions={<TourHelpButton onClick={startTour} />}
      />
      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-brand text-white rounded-md p-8" data-testid="docente-checkin" data-tour="docente-checkin">
          <div className="text-xs uppercase tracking-wider opacity-80 font-bold">Hora actual</div>
          <div className="font-display text-5xl font-black mt-1">
            {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, ...(tz ? { timeZone: tz } : {}) })}
          </div>
          {sugerida && (
            <div className="mt-4 text-sm opacity-90" data-testid="docente-clase-sugerida">
              {estado.siguiente === "entrada" ? "Próxima clase" : "Clase actual"}: <strong>{sugerida.etiqueta}</strong> · {sugerida.hora_inicio}–{sugerida.hora_fin}
              {sugerida.centro_nombre ? ` · ${sugerida.centro_nombre}` : ""}
            </div>
          )}
          {geoMsg && <div className="mt-3 text-sm flex items-center gap-2"><MapPin size={16} /> {geoMsg}</div>}
          <div className="mt-6">
            {estado && !estado.checado_web ? (
              <div className="text-sm opacity-90">Tu escuela registra la asistencia solo en el checador del plantel.</div>
            ) : (
              <Button onClick={checar} disabled={busy || !estado} className="bg-white text-brand hover:bg-slate-100 h-12 px-6 text-base font-bold" data-testid="docente-checar">
                {estado?.siguiente === "salida" ? <SignOut size={20} className="mr-2" /> : <SignIn size={20} className="mr-2" />}
                {busy ? "Registrando…" : estado?.siguiente === "salida" ? "Registrar salida" : "Registrar entrada"}
              </Button>
            )}
            {estado?.marcas?.length > 0 && (
              <div className="mt-4 text-xs opacity-80">
                Hoy: {estado.marcas.map((m) => `${m.tipo === "entrada" ? "Entrada" : "Salida"} ${fmtHora(m.hora, tz)}${m.cuenta === false ? " (fuera del plantel)" : ""}`).join(" · ")}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="docente-quincena" data-tour="docente-quincena">
          <div className="label-uppercase mb-3">Esta quincena</div>
          {quincena ? (
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-slate-500">Programadas</dt><dd className="font-semibold text-right">{quincena.programadas}</dd>
              <dt className="text-slate-500">Impartidas</dt><dd className="font-semibold text-right">{quincena.impartidas}</dd>
              <dt className="text-slate-500">Faltas</dt><dd className={`font-semibold text-right ${quincena.faltas ? "text-red-700" : ""}`}>{quincena.faltas}</dd>
              <dt className="text-slate-500">Por revisar</dt><dd className={`font-semibold text-right ${quincena.por_revisar ? "text-orange-700" : ""}`}>{quincena.por_revisar}</dd>
              <dt className="text-slate-500">Suplencias</dt><dd className="font-semibold text-right">{quincena.suplencias}</dd>
              <dt className="text-slate-500">Horas-clase a pagar</dt><dd className="font-bold text-right text-brand">{quincena.pagables}</dd>
            </dl>
          ) : <div className="text-sm text-slate-500">Cargando…</div>}
          {quincena?.por_revisar > 0 && (
            <p className="text-xs text-slate-500 mt-3">"Por revisar": te faltó checar la entrada o la salida de un bloque. Acude con RH.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="docente-hoy">
          <div className="px-4 py-3 border-b border-slate-200 label-uppercase">Hoy</div>
          <table className="data-table" data-testid="docente-hoy">
            <tbody>
              {clasesHoy.length === 0 && <tr><td className="text-slate-500 text-center py-6">Hoy no tienes clases</td></tr>}
              {clasesHoy.map((c) => (
                <tr key={`${c.clase_id}-${c.origen}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{c.hora_inicio}–{c.hora_fin}</td>
                  <td>
                    <div className="font-semibold">{c.tipo === "clase" ? n.materias[c.materia_id] || "Clase" : c.actividad || "Administrativo"}</div>
                    <div className="text-xs text-slate-500">
                      {[n.grupos[c.grupo_id], n.centros[c.centro_id], c.origen === "suplencia" && "Suplencia", c.origen === "extra" && c.actividad].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td><Chip estatus={c.estatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center">
            <span className="label-uppercase">Mi horario</span>
            {carga?.ciclo && <span className="text-xs text-slate-500">Ciclo {carga.ciclo}</span>}
          </div>
          <div className="divide-y divide-slate-100" data-testid="docente-horario" data-tour="docente-horario">
            {(!carga || carga.clases.length === 0) && <div className="p-6 text-sm text-slate-500 text-center">Aún no tienes carga asignada</div>}
            {[1, 2, 3, 4, 5, 6, 0].filter((d) => porDia[d]).map((d) => (
              <div key={d} className="px-4 py-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{DIAS[d]}</div>
                {porDia[d].map((c) => (
                  <div key={c.id} className="text-sm flex gap-3">
                    <span className="font-mono text-xs text-slate-500 w-24 shrink-0">{c.hora_inicio}–{c.hora_fin}</span>
                    <span>{c.etiqueta}{c.centro_nombre ? <span className="text-slate-500"> · {c.centro_nombre}</span> : ""}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
